/**
 * Состояние раздела базы знаний: выбранное пространство, дерево страниц и
 * открытая страница.
 *
 * Живёт стором, а не в экране, потому что дерево показывает сайдбар, а
 * страницу — основная область: два места читают одно и то же, и грузить дерево
 * дважды незачем.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  getPage,
  getTree,
  listIssueLinks,
  listSpaces,
  toTree,
  type Page,
  type PageBrief,
  type PageIssueLink,
  type Space,
} from './api'

const SPACE_KEY = 'tracker-kb-space'

export const useKnowledgeStore = defineStore('knowledge', () => {
  const spaces = ref<Space[]>([])
  const spaceId = ref('')
  const pages = ref<PageBrief[]>([])
  const page = ref<Page | null>(null)
  const links = ref<PageIssueLink[]>([])
  const isLoading = ref(false)
  const error = ref('')

  const tree = computed(() => toTree(pages.value))
  const space = computed(() => spaces.value.find((entry) => entry.id === spaceId.value) ?? null)

  /** Выбранное пространство помним между заходами: оно у человека обычно одно. */
  const remember = (id: string) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(SPACE_KEY, id)
  }

  const stored = () =>
    typeof window === 'undefined' ? '' : (window.localStorage.getItem(SPACE_KEY) ?? '')

  const loadTree = async (id: string) => {
    spaceId.value = id
    remember(id)
    pages.value = await getTree(id)
  }

  const ensureSpaces = async () => {
    if (spaces.value.length) return

    isLoading.value = true
    error.value = ''

    try {
      spaces.value = await listSpaces()

      const wanted = spaces.value.find((entry) => entry.id === stored()) ?? spaces.value[0]

      if (wanted) await loadTree(wanted.id)
    } catch {
      error.value = 'load'
    } finally {
      isLoading.value = false
    }
  }

  const selectSpace = async (id: string) => {
    page.value = null
    links.value = []
    await loadTree(id)
  }

  const openPage = async (pageId: string) => {
    isLoading.value = true
    error.value = ''

    try {
      page.value = await getPage(pageId)
      // Связи грузим следом: страница читается и без них, а ждать их значит
      // держать текст пустым лишние полсекунды.
      links.value = await listIssueLinks(pageId)

      if (page.value.spaceId && page.value.spaceId !== spaceId.value) {
        await loadTree(page.value.spaceId)
      }
    } catch {
      error.value = 'page'
    } finally {
      isLoading.value = false
    }
  }

  return {
    spaces,
    spaceId,
    space,
    pages,
    tree,
    page,
    links,
    isLoading,
    error,
    ensureSpaces,
    selectSpace,
    openPage,
  }
})
